const MODEL_VIEWER_URL = 'https://ajax.googleapis.com/ajax/libs/model-viewer/3.5.0/model-viewer.min.js';
const MAX_TEXTURE = 1024;
const MIN_METRES = 0.05;
const MAX_METRES = 5;
const DEPTH = 0.05;
const FRAME_FACE = 0.014;
const FRAME_GAP = 0.008;

let modelViewerImport = null;
let woodTexturePromise = null;
const frameTextures = new Map();

function loadModelViewer() {
  if (customElements.get('model-viewer')) return Promise.resolve();
  if (!modelViewerImport) {
    modelViewerImport = import(MODEL_VIEWER_URL).catch(() => {
      modelViewerImport = null;
      throw new Error('model-viewer');
    });
  }
  return modelViewerImport;
}

function isIos() {
  return (
    /iP(hone|ad|od)/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

/** Safari reports rel="ar"; Chrome, Firefox, Edge and the Google app on iOS don't, but still open Quick Look. */
function supportsQuickLook() {
  const anchor = document.createElement('a');
  if (anchor.relList && anchor.relList.supports && anchor.relList.supports('ar')) return true;
  return isIos() && /CriOS\/|FxiOS\/|EdgiOS\/|GSA\/|DuckDuckGo\//.test(navigator.userAgent);
}

function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function isAndroid() {
  const platform = navigator.userAgentData && navigator.userAgentData.platform;
  if (platform) return /android/i.test(platform);
  return /android/i.test(navigator.userAgent);
}

function absoluteUrl(value) {
  if (!value) return '';
  try {
    return new URL(value, window.location.href).toString();
  } catch (error) {
    return value;
  }
}

function clampMetres(value, fallback) {
  const number = Number(value);
  if (!Number.isFinite(number) || number <= 0) return fallback;
  return Math.min(MAX_METRES, Math.max(MIN_METRES, number));
}

function concatBytes(parts) {
  const total = parts.reduce((sum, part) => sum + part.byteLength, 0);
  const out = new Uint8Array(total);
  let offset = 0;
  for (const part of parts) {
    const bytes = part instanceof Uint8Array ? part : new Uint8Array(part);
    out.set(bytes, offset);
    offset += bytes.byteLength;
  }
  return out;
}

function padBytes(bytes, padByte = 0) {
  const extra = (4 - (bytes.length % 4)) % 4;
  if (!extra) return bytes;
  const out = new Uint8Array(bytes.length + extra);
  out.set(bytes);
  out.fill(padByte, bytes.length);
  return out;
}

function u16(value) {
  const bytes = new Uint8Array(2);
  new DataView(bytes.buffer).setUint16(0, value, true);
  return bytes;
}

function u32(value) {
  const bytes = new Uint8Array(4);
  new DataView(bytes.buffer).setUint32(0, value, true);
  return bytes;
}

function f32(values) {
  return new Uint8Array(Float32Array.from(values).buffer);
}

function crc32(data) {
  let crc = 0xffffffff;
  for (let i = 0; i < data.length; i += 1) {
    crc ^= data[i];
    for (let bit = 0; bit < 8; bit += 1) {
      crc = (crc >>> 1) ^ (0xedb88320 & -(crc & 1));
    }
  }
  return (crc ^ 0xffffffff) >>> 0;
}

function fmtNum(value) {
  const rounded = Math.round(value * 1e6) / 1e6;
  return Object.is(rounded, -0) ? '0' : String(rounded);
}

function fmtVec3(values, index) {
  return `(${fmtNum(values[index])}, ${fmtNum(values[index + 1])}, ${fmtNum(values[index + 2])})`;
}

async function loadArtworkBitmap(url) {
  const response = await fetch(url, { mode: 'cors' });
  if (!response.ok) throw new Error('image');
  return createImageBitmap(await response.blob());
}

/** Centre-crops the artwork to the print's proportions so it isn't stretched across the canvas. */
async function artworkTexture(bitmap, aspect) {
  let sx = 0;
  let sy = 0;
  let sw = bitmap.width;
  let sh = bitmap.height;
  if (sw / sh > aspect) {
    sw = sh * aspect;
    sx = (bitmap.width - sw) / 2;
  } else {
    sh = sw / aspect;
    sy = (bitmap.height - sh) / 2;
  }
  const scale = Math.min(1, MAX_TEXTURE / Math.max(sw, sh));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(sw * scale));
  canvas.height = Math.max(1, Math.round(sh * scale));
  const context = canvas.getContext('2d');
  if (!context) throw new Error('canvas');
  context.drawImage(bitmap, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob((result) => (result ? resolve(result) : reject(new Error('texture'))), 'image/jpeg', 0.88);
  });
  return new Uint8Array(await blob.arrayBuffer());
}

function hash2(x, y) {
  const s = Math.sin(x * 127.1 + y * 311.7) * 43758.5453;
  return s - Math.floor(s);
}

const PAINTED_FRAMES = {
  white: [244, 242, 238],
  black: [36, 34, 32],
};

function makeFrameJpeg(kind) {
  if (!PAINTED_FRAMES[kind]) return makeWoodJpeg();
  if (frameTextures.has(kind)) return frameTextures.get(kind);
  const [r, g, b] = PAINTED_FRAMES[kind];
  const promise = new Promise((resolve, reject) => {
    const size = 64;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const context = canvas.getContext('2d');
    if (!context) {
      reject(new Error('canvas'));
      return;
    }
    const image = context.createImageData(size, size);
    for (let i = 0; i < size * size; i += 1) {
      const n = Math.round((hash2(i % size, Math.floor(i / size)) - 0.5) * 6);
      image.data[i * 4] = r + n;
      image.data[i * 4 + 1] = g + n;
      image.data[i * 4 + 2] = b + n;
      image.data[i * 4 + 3] = 255;
    }
    context.putImageData(image, 0, 0);
    canvas.toBlob((result) => (result ? resolve(result.arrayBuffer()) : reject(new Error('frame'))), 'image/jpeg', 0.9);
  }).then((buffer) => new Uint8Array(buffer));
  frameTextures.set(kind, promise);
  return promise;
}

function makeWoodJpeg() {
  if (woodTexturePromise) return woodTexturePromise;
  woodTexturePromise = new Promise((resolve, reject) => {
    const size = 256;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const context = canvas.getContext('2d');
    if (!context) {
      reject(new Error('canvas'));
      return;
    }
    const image = context.createImageData(size, size);
    for (let y = 0; y < size; y += 1) {
      for (let x = 0; x < size; x += 1) {
        const grain = Math.sin(x * 0.085 + Math.sin(y * 0.03) * 3.4) * 0.5 + 0.5;
        const fine = Math.sin(x * 0.62 + y * 0.04) * 0.07;
        const n = (hash2(x, y) - 0.5) * 0.05;
        const t = Math.min(1, Math.max(0, 0.42 + grain * 0.4 + fine + n));
        const i = (y * size + x) * 4;
        image.data[i] = Math.round(214 + t * 28);
        image.data[i + 1] = Math.round(198 + t * 24);
        image.data[i + 2] = Math.round(168 + t * 20);
        image.data[i + 3] = 255;
      }
    }
    context.putImageData(image, 0, 0);
    canvas.toBlob((result) => (result ? resolve(result.arrayBuffer()) : reject(new Error('wood'))), 'image/jpeg', 0.86);
  }).then((buffer) => new Uint8Array(buffer));
  return woodTexturePromise;
}

function createMesh() {
  return { positions: [], normals: [], uvs: [], indices: [] };
}

function addQuad(mesh, a, b, c, d, expectedNormal, uvs) {
  const ab = [b[0] - a[0], b[1] - a[1], b[2] - a[2]];
  const ac = [c[0] - a[0], c[1] - a[1], c[2] - a[2]];
  const cross = [ab[1] * ac[2] - ab[2] * ac[1], ab[2] * ac[0] - ab[0] * ac[2], ab[0] * ac[1] - ab[1] * ac[0]];
  const dot = cross[0] * expectedNormal[0] + cross[1] * expectedNormal[1] + cross[2] * expectedNormal[2];
  const corners = dot < 0 ? [a, d, c, b] : [a, b, c, d];
  const uv =
    uvs && dot >= 0
      ? uvs
      : uvs && dot < 0
        ? [uvs[0], uvs[1], uvs[6], uvs[7], uvs[4], uvs[5], uvs[2], uvs[3]]
        : [0, 0, 1, 0, 1, 1, 0, 1];
  const length = Math.hypot(expectedNormal[0], expectedNormal[1], expectedNormal[2]) || 1;
  const normal = [expectedNormal[0] / length, expectedNormal[1] / length, expectedNormal[2] / length];
  const index = mesh.positions.length / 3;
  mesh.positions.push(...corners[0], ...corners[1], ...corners[2], ...corners[3]);
  mesh.normals.push(...normal, ...normal, ...normal, ...normal);
  mesh.uvs.push(...uv);
  mesh.indices.push(index, index + 1, index + 2, index, index + 2, index + 3);
}

function meshExtents(mesh) {
  const min = [Infinity, Infinity, Infinity];
  const max = [-Infinity, -Infinity, -Infinity];
  for (let i = 0; i < mesh.positions.length; i += 3) {
    min[0] = Math.min(min[0], mesh.positions[i]);
    min[1] = Math.min(min[1], mesh.positions[i + 1]);
    min[2] = Math.min(min[2], mesh.positions[i + 2]);
    max[0] = Math.max(max[0], mesh.positions[i]);
    max[1] = Math.max(max[1], mesh.positions[i + 1]);
    max[2] = Math.max(max[2], mesh.positions[i + 2]);
  }
  return { min, max };
}

function wallToFloor(mesh) {
  const positions = [];
  const normals = [];
  for (let i = 0; i < mesh.positions.length; i += 3) {
    positions.push(mesh.positions[i], mesh.positions[i + 2], -mesh.positions[i + 1]);
  }
  for (let i = 0; i < mesh.normals.length; i += 3) {
    normals.push(mesh.normals[i], mesh.normals[i + 2], -mesh.normals[i + 1]);
  }
  return { positions, normals, uvs: mesh.uvs.slice(), indices: mesh.indices.slice() };
}

function buildArtworkMeshes(widthM, heightM, addFrame) {
  const hx = widthM / 2;
  const hy = heightM / 2;
  const z0 = 0;
  const z1 = DEPTH;
  const painting = createMesh();
  const sides = createMesh();
  const back = createMesh();
  const frame = createMesh();
  const gap = createMesh();
  const wrapU = Math.min(0.45, DEPTH / Math.max(widthM, 0.01));
  const wrapV = Math.min(0.45, DEPTH / Math.max(heightM, 0.01));

  addQuad(painting, [-hx, -hy, z1], [hx, -hy, z1], [hx, hy, z1], [-hx, hy, z1], [0, 0, 1], [0, 0, 1, 0, 1, 1, 0, 1]);

  addQuad(
    sides,
    [hx, -hy, z0],
    [hx, hy, z0],
    [hx, hy, z1],
    [hx, -hy, z1],
    [1, 0, 0],
    [1 - wrapU, 0, 1 - wrapU, 1, 1, 1, 1, 0],
  );
  addQuad(
    sides,
    [-hx, -hy, z0],
    [-hx, -hy, z1],
    [-hx, hy, z1],
    [-hx, hy, z0],
    [-1, 0, 0],
    [wrapU, 0, 0, 0, 0, 1, wrapU, 1],
  );
  addQuad(
    sides,
    [-hx, hy, z0],
    [-hx, hy, z1],
    [hx, hy, z1],
    [hx, hy, z0],
    [0, 1, 0],
    [0, 1 - wrapV, 0, 1, 1, 1, 1, 1 - wrapV],
  );
  addQuad(
    sides,
    [-hx, -hy, z0],
    [hx, -hy, z0],
    [hx, -hy, z1],
    [-hx, -hy, z1],
    [0, -1, 0],
    [0, wrapV, 1, wrapV, 1, 0, 0, 0],
  );
  addQuad(back, [hx, -hy, z0], [-hx, -hy, z0], [-hx, hy, z0], [hx, hy, z0], [0, 0, -1]);

  if (addFrame) {
    const g = FRAME_GAP;
    const f = FRAME_FACE;
    const ix0 = -hx - g;
    const ix1 = hx + g;
    const iy0 = -hy - g;
    const iy1 = hy + g;
    const ox0 = ix0 - f;
    const ox1 = ix1 + f;
    const oy0 = iy0 - f;
    const oy1 = iy1 + f;

    addQuad(
      frame,
      [ox0, oy0, z1],
      [ox1, oy0, z1],
      [ox1, iy0, z1],
      [ox0, iy0, z1],
      [0, 0, 1],
      [0, 0, 1, 0, 1, 0.18, 0, 0.18],
    );
    addQuad(
      frame,
      [ox0, iy1, z1],
      [ox1, iy1, z1],
      [ox1, oy1, z1],
      [ox0, oy1, z1],
      [0, 0, 1],
      [0, 0.82, 1, 0.82, 1, 1, 0, 1],
    );
    addQuad(
      frame,
      [ox0, iy0, z1],
      [ix0, iy0, z1],
      [ix0, iy1, z1],
      [ox0, iy1, z1],
      [0, 0, 1],
      [0, 0, 0.18, 0, 0.18, 1, 0, 1],
    );
    addQuad(
      frame,
      [ix1, iy0, z1],
      [ox1, iy0, z1],
      [ox1, iy1, z1],
      [ix1, iy1, z1],
      [0, 0, 1],
      [0.82, 0, 1, 0, 1, 1, 0.82, 1],
    );

    addQuad(frame, [ox1, oy0, z0], [ox1, oy1, z0], [ox1, oy1, z1], [ox1, oy0, z1], [1, 0, 0]);
    addQuad(frame, [ox0, oy0, z0], [ox0, oy0, z1], [ox0, oy1, z1], [ox0, oy1, z0], [-1, 0, 0]);
    addQuad(frame, [ox0, oy1, z0], [ox0, oy1, z1], [ox1, oy1, z1], [ox1, oy1, z0], [0, 1, 0]);
    addQuad(frame, [ox0, oy0, z0], [ox1, oy0, z0], [ox1, oy0, z1], [ox0, oy0, z1], [0, -1, 0]);

    addQuad(frame, [ix1, iy0, z0], [ix1, iy0, z1], [ix1, iy1, z1], [ix1, iy1, z0], [-1, 0, 0]);
    addQuad(frame, [ix0, iy0, z0], [ix0, iy1, z0], [ix0, iy1, z1], [ix0, iy0, z1], [1, 0, 0]);
    addQuad(frame, [ix0, iy1, z0], [ix1, iy1, z0], [ix1, iy1, z1], [ix0, iy1, z1], [0, -1, 0]);
    addQuad(frame, [ix0, iy0, z0], [ix0, iy0, z1], [ix1, iy0, z1], [ix1, iy0, z0], [0, 1, 0]);

    addQuad(frame, [ox1, oy0, z0], [ox0, oy0, z0], [ox0, iy0, z0], [ox1, iy0, z0], [0, 0, -1]);
    addQuad(frame, [ox1, iy1, z0], [ox0, iy1, z0], [ox0, oy1, z0], [ox1, oy1, z0], [0, 0, -1]);
    addQuad(frame, [ix0, iy0, z0], [ox0, iy0, z0], [ox0, iy1, z0], [ix0, iy1, z0], [0, 0, -1]);
    addQuad(frame, [ox1, iy0, z0], [ix1, iy0, z0], [ix1, iy1, z0], [ox1, iy1, z0], [0, 0, -1]);

    const zb = 0.002;
    addQuad(gap, [ix0, iy0, zb], [ix1, iy0, zb], [ix1, -hy, zb], [ix0, -hy, zb], [0, 0, 1]);
    addQuad(gap, [ix0, hy, zb], [ix1, hy, zb], [ix1, iy1, zb], [ix0, iy1, zb], [0, 0, 1]);
    addQuad(gap, [ix0, -hy, zb], [-hx, -hy, zb], [-hx, hy, zb], [ix0, hy, zb], [0, 0, 1]);
    addQuad(gap, [hx, -hy, zb], [ix1, -hy, zb], [ix1, hy, zb], [hx, hy, zb], [0, 0, 1]);
  }

  return { painting, sides, back, frame, gap, addFrame };
}

function packMesh(mesh, offset) {
  const positions = f32(mesh.positions);
  const normals = f32(mesh.normals);
  // glTF samples textures from the top-left; the mesh UVs use USD's bottom-left origin.
  const uvs = f32(mesh.uvs.map((value, index) => (index % 2 ? 1 - value : value)));
  const indexBytes = new Uint8Array(Uint16Array.from(mesh.indices).buffer);
  const posPad = padBytes(positions);
  const nrmPad = padBytes(normals);
  const uvPad = padBytes(uvs);
  const idxPad = padBytes(indexBytes);
  const extents = meshExtents(mesh);
  const views = {
    position: { buffer: 0, byteOffset: offset, byteLength: positions.byteLength, target: 34962 },
    normal: { buffer: 0, byteOffset: offset + posPad.byteLength, byteLength: normals.byteLength, target: 34962 },
    uv: {
      buffer: 0,
      byteOffset: offset + posPad.byteLength + nrmPad.byteLength,
      byteLength: uvs.byteLength,
      target: 34962,
    },
    indices: {
      buffer: 0,
      byteOffset: offset + posPad.byteLength + nrmPad.byteLength + uvPad.byteLength,
      byteLength: indexBytes.byteLength,
      target: 34963,
    },
  };
  const bin = concatBytes([posPad, nrmPad, uvPad, idxPad]);
  return {
    bin,
    views,
    vertexCount: mesh.positions.length / 3,
    indexCount: mesh.indices.length,
    min: extents.min,
    max: extents.max,
    byteLength: bin.byteLength,
  };
}

function buildGlb(imageBytes, woodBytes, widthM, heightM, addFrame, litArtwork = false) {
  const meshes = buildArtworkMeshes(widthM, heightM, addFrame);
  const packed = [];
  const parts = [
    { mesh: meshes.painting, material: 0, texcoord: true },
    { mesh: meshes.sides, material: 0, texcoord: true },
    { mesh: meshes.back, material: 1, texcoord: false },
  ];
  if (addFrame) {
    parts.push({ mesh: meshes.frame, material: 2, texcoord: true });
    parts.push({ mesh: meshes.gap, material: 1, texcoord: false });
  }

  let offset = 0;
  const chunks = [];
  for (const part of parts) {
    const item = packMesh(part.mesh, offset);
    packed.push(item);
    chunks.push(item.bin);
    offset += item.byteLength;
  }

  const imageViews = [];
  const images = [{ bytes: imageBytes, mime: 'image/jpeg', name: 'artwork' }];
  if (addFrame) images.push({ bytes: woodBytes, mime: 'image/jpeg', name: 'wood' });
  for (const image of images) {
    imageViews.push({ buffer: 0, byteOffset: offset, byteLength: image.bytes.byteLength });
    const padded = padBytes(image.bytes);
    chunks.push(padded);
    offset += padded.byteLength;
  }

  const bin = concatBytes(chunks);
  const bufferViews = [];
  const accessors = [];
  const primitives = [];

  packed.forEach((item, index) => {
    const posView = bufferViews.length;
    bufferViews.push(item.views.position);
    const nrmView = bufferViews.length;
    bufferViews.push(item.views.normal);
    let uvView = -1;
    if (parts[index].texcoord) {
      uvView = bufferViews.length;
      bufferViews.push(item.views.uv);
    }
    const idxView = bufferViews.length;
    bufferViews.push(item.views.indices);

    const posAcc = accessors.length;
    accessors.push({
      bufferView: posView,
      componentType: 5126,
      count: item.vertexCount,
      type: 'VEC3',
      min: item.min,
      max: item.max,
    });
    const nrmAcc = accessors.length;
    accessors.push({ bufferView: nrmView, componentType: 5126, count: item.vertexCount, type: 'VEC3' });
    const attributes = { POSITION: posAcc, NORMAL: nrmAcc };
    if (uvView >= 0) {
      const uvAcc = accessors.length;
      accessors.push({ bufferView: uvView, componentType: 5126, count: item.vertexCount, type: 'VEC2' });
      attributes.TEXCOORD_0 = uvAcc;
    }
    const idxAcc = accessors.length;
    accessors.push({ bufferView: idxView, componentType: 5123, count: item.indexCount, type: 'SCALAR' });
    primitives.push({ attributes, indices: idxAcc, material: parts[index].material });
  });

  images.forEach((_, index) => {
    bufferViews.push(imageViews[index]);
  });

  const materials = [
    {
      name: 'ArtworkMaterial',
      pbrMetallicRoughness: {
        baseColorTexture: { index: 0 },
        metallicFactor: 0,
        roughnessFactor: 1,
      },
      ...(litArtwork ? {} : { extensions: { KHR_materials_unlit: {} } }),
    },
    {
      name: 'ShadowGap',
      pbrMetallicRoughness: {
        baseColorFactor: [0.11, 0.09, 0.07, 1],
        metallicFactor: 0,
        roughnessFactor: 1,
      },
      extensions: { KHR_materials_unlit: {} },
    },
  ];
  if (addFrame) {
    materials.push({
      name: 'RawAsh',
      pbrMetallicRoughness: {
        baseColorTexture: { index: 1 },
        metallicFactor: 0,
        roughnessFactor: 0.72,
      },
    });
  }

  const json = padBytes(
    new TextEncoder().encode(
      JSON.stringify({
        asset: { version: '2.0', generator: 'artwork-preview' },
        extensionsUsed: ['KHR_materials_unlit'],
        scene: 0,
        scenes: [{ nodes: [0] }],
        nodes: [{ mesh: 0, name: 'Artwork' }],
        meshes: [{ name: 'Artwork', primitives }],
        materials,
        textures: images.map((_, index) => ({ source: index })),
        images: images.map((image, index) => ({
          bufferView: bufferViews.length - images.length + index,
          mimeType: image.mime,
          name: image.name,
        })),
        buffers: [{ byteLength: bin.byteLength }],
        bufferViews,
        accessors,
      }),
    ),
    0x20,
  );

  return concatBytes([
    new TextEncoder().encode('glTF'),
    u32(2),
    u32(12 + 8 + json.byteLength + 8 + bin.byteLength),
    u32(json.byteLength),
    new TextEncoder().encode('JSON'),
    json,
    u32(bin.byteLength),
    new Uint8Array([0x42, 0x49, 0x4e, 0x00]),
    bin,
  ]);
}

function usdaMesh(name, mesh, materialPath, withUv) {
  const floor = wallToFloor(mesh);
  const extents = meshExtents(floor);
  const points = [];
  for (let i = 0; i < floor.positions.length; i += 3) points.push(fmtVec3(floor.positions, i));
  const normals = [];
  for (let i = 0; i < floor.normals.length; i += 3) normals.push(fmtVec3(floor.normals, i));
  const counts = [];
  for (let i = 0; i < floor.indices.length; i += 3) counts.push('3');
  const st = [];
  if (withUv) {
    for (let i = 0; i < floor.uvs.length; i += 2) {
      st.push(`(${fmtNum(floor.uvs[i])}, ${fmtNum(floor.uvs[i + 1])})`);
    }
  }
  return `    def Mesh "${name}" (
        prepend apiSchemas = ["MaterialBindingAPI"]
    )
    {
        float3[] extent = [${fmtVec3(extents.min, 0)}, ${fmtVec3(extents.max, 0)}]
        int[] faceVertexCounts = [${counts.join(', ')}]
        int[] faceVertexIndices = [${floor.indices.join(', ')}]
        normal3f[] normals = [${normals.join(', ')}] (
            interpolation = "vertex"
        )
        point3f[] points = [${points.join(', ')}]
${
  withUv
    ? `        texCoord2f[] primvars:st = [${st.join(', ')}] (
            interpolation = "vertex"
        )`
    : ''
}
        uniform token subdivisionScheme = "none"
        rel material:binding = ${materialPath}
    }`;
}

function usdaTextureMaterial(name, fileName) {
  return `    def Material "${name}"
    {
        token outputs:surface.connect = </Root/${name}/PreviewSurface.outputs:surface>

        def Shader "PreviewSurface"
        {
            uniform token info:id = "UsdPreviewSurface"
            color3f inputs:diffuseColor.connect = </Root/${name}/ColorTex.outputs:rgb>
            float inputs:metallic = 0
            float inputs:roughness = ${name === 'FrameMaterial' ? '0.72' : '1'}
            int inputs:useSpecularWorkflow = 0
            token outputs:surface
        }

        def Shader "ColorTex"
        {
            uniform token info:id = "UsdUVTexture"
            asset inputs:file = @./${fileName}@
            float2 inputs:st.connect = </Root/${name}/STReader.outputs:result>
            token inputs:wrapS = "clamp"
            token inputs:wrapT = "clamp"
            token outputs:rgb
        }

        def Shader "STReader"
        {
            uniform token info:id = "UsdPrimvarReader_float2"
            token inputs:varname = "st"
            float2 outputs:result
        }
    }`;
}

function usdaColorMaterial(name, color, roughness) {
  return `    def Material "${name}"
    {
        token outputs:surface.connect = </Root/${name}/PreviewSurface.outputs:surface>

        def Shader "PreviewSurface"
        {
            uniform token info:id = "UsdPreviewSurface"
            color3f inputs:diffuseColor = (${color.join(', ')})
            float inputs:metallic = 0
            float inputs:roughness = ${roughness}
            int inputs:useSpecularWorkflow = 0
            token outputs:surface
        }
    }`;
}

function buildUsda(widthM, heightM, addFrame) {
  const meshes = buildArtworkMeshes(widthM, heightM, addFrame);
  const parts = [
    usdaMesh('Painting', meshes.painting, '</Root/ArtworkMaterial>', true),
    usdaMesh('Sides', meshes.sides, '</Root/ArtworkMaterial>', true),
    usdaMesh('Back', meshes.back, '</Root/GapMaterial>', false),
  ];
  const materials = [
    usdaTextureMaterial('ArtworkMaterial', 'artwork.jpg'),
    usdaColorMaterial('GapMaterial', ['0.11', '0.09', '0.07'], 1),
  ];
  if (addFrame) {
    parts.push(usdaMesh('Frame', meshes.frame, '</Root/FrameMaterial>', true));
    parts.push(usdaMesh('Gap', meshes.gap, '</Root/GapMaterial>', false));
    materials.push(usdaTextureMaterial('FrameMaterial', 'wood.jpg'));
  }

  return `#usda 1.0
(
    defaultPrim = "Root"
    metersPerUnit = 1
    upAxis = "Y"
    customLayerData = {
        dictionary Apple = {
            bool arQuickLookCompatible = 1
        }
    }
)

def Xform "Root" (
    prepend apiSchemas = ["Preliminary_AnchoringAPI"]
)
{
    uniform token preliminary:anchoring:type = "plane"
    uniform token preliminary:planeAnchoring:alignment = "vertical"

${parts.join('\n\n')}

${materials.join('\n\n')}
}
`;
}

function buildUsdz(imageBytes, woodBytes, widthM, heightM, addFrame) {
  const files = [
    { name: 'model.usda', data: new TextEncoder().encode(buildUsda(widthM, heightM, addFrame)) },
    { name: 'artwork.jpg', data: imageBytes },
  ];
  if (addFrame) files.push({ name: 'wood.jpg', data: woodBytes });
  const locals = [];
  const centrals = [];
  let offset = 0;

  for (const file of files) {
    const nameBytes = new TextEncoder().encode(file.name);
    const extraLength = (64 - ((offset + 30 + nameBytes.length) % 64)) % 64;
    const extra = new Uint8Array(extraLength);
    const crc = crc32(file.data);
    const local = concatBytes([
      u32(0x04034b50),
      u16(20),
      u16(0),
      u16(0),
      u16(0),
      u16(0),
      u32(crc),
      u32(file.data.byteLength),
      u32(file.data.byteLength),
      u16(nameBytes.length),
      u16(extraLength),
      nameBytes,
      extra,
      file.data,
    ]);
    locals.push(local);
    centrals.push(
      concatBytes([
        u32(0x02014b50),
        u16(20),
        u16(20),
        u16(0),
        u16(0),
        u16(0),
        u16(0),
        u32(crc),
        u32(file.data.byteLength),
        u32(file.data.byteLength),
        u16(nameBytes.length),
        u16(extraLength),
        u16(0),
        u16(0),
        u16(0),
        u32(0),
        u32(offset),
        nameBytes,
        extra,
      ]),
    );
    offset += local.byteLength;
  }

  const centralDir = concatBytes(centrals);
  return concatBytes([
    ...locals,
    centralDir,
    u32(0x06054b50),
    u16(0),
    u16(0),
    u16(files.length),
    u16(files.length),
    u32(centralDir.byteLength),
    u32(offset),
    u16(0),
  ]);
}

function launchQuickLook(usdzUrl) {
  const anchor = document.createElement('a');
  const image = document.createElement('img');
  image.alt = '';
  image.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
  anchor.rel = 'ar';
  anchor.href = usdzUrl;
  anchor.appendChild(image);
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
}

function parseJson(value, fallback) {
  try {
    return value ? JSON.parse(value) : fallback;
  } catch (error) {
    return fallback;
  }
}

function normaliseSpec(spec) {
  if (!spec) return null;
  const frame = spec.frame === true ? 'natural' : spec.frame || 'none';
  return {
    id: spec.id ? String(spec.id) : '',
    w: clampMetres(spec.w, 0.8),
    h: clampMetres(spec.h, 1),
    frame,
    label: spec.label || '',
    sizeValue: spec.sizeValue || '',
    frameValue: spec.frameValue || '',
    frameLabel: spec.frameLabel || '',
  };
}

function specKey(spec) {
  return `${spec.w}x${spec.h}:${spec.frame}`;
}

function isPhone() {
  return window.matchMedia('(pointer: coarse)').matches && Math.min(window.screen.width, window.screen.height) < 820;
}

function uniqueChoices(list, valueOf, labelOf) {
  const seen = new Map();
  list.forEach((spec) => {
    const value = valueOf(spec);
    if (!value || seen.has(value)) return;
    seen.set(value, labelOf(spec) || value);
  });
  return [...seen.entries()].map(([value, label]) => ({ value, label }));
}

function setOptionValue(root, value) {
  if (!value) return;
  root.querySelectorAll('select[data-variant-option]').forEach((select) => {
    if (![...select.options].some((option) => option.value === value)) return;
    select.value = value;
    select.dataset.variantOptionChosenValue = value;
  });
  root.querySelectorAll('input[data-variant-option-value]').forEach((input) => {
    if (input.value !== value) return;
    input.checked = true;
    const group = input.closest('[data-variant-option]');
    if (group) group.dataset.variantOptionChosenValue = value;
  });
}

function syncVariantOptions(spec) {
  const selection = document.querySelector('.product-main variant-selection, .product-template variant-selection');
  const root = selection ? selection.closest('.shopify-section') || document : document;
  setOptionValue(root, spec.sizeValue);
  setOptionValue(root, spec.frameValue);
}

class ArtworkVisualiser {
  /**
   * @param {HTMLElement} root
   */
  constructor(root) {
    this.root = root;
    this.arButton = root.querySelector('[data-artwork-ar-button]');
    this.uploadButton = root.querySelector('[data-artwork-upload-button]');
    this.statusEl = root.querySelector('[data-artwork-status]');
    this.sizeEl = root.querySelector('[data-artwork-size]');
    this.modal = root.querySelector('[data-kjv]');
    this.viewerHost = root.querySelector('[data-artwork-viewer]');
    this.viewer = null;
    this.busy = false;
    this.imageUrl = absoluteUrl(root.dataset.artworkImage);
    this.bitmap = null;
    this.textures = new Map();
    this.builds = new Map();
    this.ready = new Map();

    this.specs = new Map();
    parseJson(root.dataset.artworkVariants, []).forEach((raw) => {
      const spec = normaliseSpec(raw);
      if (spec && spec.id) this.specs.set(spec.id, spec);
    });
    this.spec =
      normaliseSpec(parseJson(root.dataset.artworkDefault, null)) ||
      normaliseSpec({
        w: root.dataset.artworkWidth,
        h: root.dataset.artworkHeight,
        frame: root.dataset.artworkFrame === 'true' ? 'natural' : 'none',
      });

    if (this.arButton) {
      this.arButton.addEventListener('click', (event) => this.start(event.currentTarget));
    }
    if (this.uploadButton) {
      this.uploadButton.addEventListener('click', () => this.showStatus(root.dataset.messageComingSoon));
    }
    this.bindModal();
    this.bindVideo();
    this.bindVariantChanges();
    this.bindPicker();
    this.setSpec(this.spec);
    this.warmModelViewer();
    this.handleArrival();
  }

  bindVideo() {
    const video = this.root.querySelector('[data-artwork-video], .kj-preview__video');
    const play = this.root.querySelector('[data-artwork-play]');
    if (!video || !play) return;
    play.addEventListener('click', () => {
      video.controls = true;
      video.play();
      play.hidden = true;
    });
  }

  bindVariantChanges() {
    if (this.specs.size < 2) return;
    document.querySelectorAll('.product-main variant-selection, .product-template variant-selection').forEach((el) => {
      el.addEventListener('variant-change', (event) => {
        const variant = event.detail && event.detail.variant;
        const spec = variant && this.specs.get(String(variant.id));
        if (spec) this.setSpec(spec);
      });
    });
  }

  bindPicker() {
    const button = this.root.querySelector('[data-artwork-change]');
    this.picker = this.root.querySelector('[data-artwork-picker]');
    if (!button || !this.picker || this.specs.size < 2) return;

    const list = [...this.specs.values()];
    this.sizeChoices = uniqueChoices(
      list,
      (spec) => spec.sizeValue,
      (spec) => spec.label.split(' · ')[0],
    );
    this.frameChoices = uniqueChoices(
      list,
      (spec) => spec.frameValue,
      (spec) => spec.frameLabel,
    );
    if (this.sizeChoices.length < 2 && this.frameChoices.length < 2) return;

    button.hidden = false;
    button.addEventListener('click', () => {
      const open = this.picker.hidden;
      this.picker.hidden = !open;
      button.setAttribute('aria-expanded', String(open));
      if (open) this.renderPicker();
    });
  }

  renderPicker() {
    if (!this.picker) return;
    const groups = [
      ['Size', this.sizeChoices, 'sizeValue'],
      ['Frame', this.frameChoices, 'frameValue'],
    ];
    this.picker.replaceChildren();
    groups.forEach(([name, choices, key]) => {
      if (!choices || choices.length < 2) return;
      const group = document.createElement('div');
      const title = document.createElement('p');
      title.className = 'kj-preview__opt-name';
      title.textContent = name;
      const row = document.createElement('div');
      row.className = 'kj-preview__opts';
      choices.forEach((choice) => {
        const chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'kj-preview__opt';
        chip.textContent = choice.label;
        chip.setAttribute('aria-pressed', String(this.spec && this.spec[key] === choice.value));
        if (this.spec && this.spec[key] === choice.value) chip.classList.add('is-selected');
        chip.addEventListener('click', () => this.chooseOption(key, choice.value));
        row.appendChild(chip);
      });
      group.append(title, row);
      this.picker.appendChild(group);
    });
  }

  chooseOption(key, value) {
    const otherKey = key === 'sizeValue' ? 'frameValue' : 'sizeValue';
    const currentOther = this.spec ? this.spec[otherKey] : '';
    const list = [...this.specs.values()];
    const next =
      list.find((spec) => spec[key] === value && spec[otherKey] === currentOther) ||
      list.find((spec) => spec[key] === value);
    if (!next) return;
    const selection = document.querySelector('.product-main variant-selection, .product-template variant-selection');
    if (selection) {
      syncVariantOptions(next);
      selection.variant = String(next.id);
    } else {
      this.setSpec(next);
    }
    this.renderPicker();
  }

  /** The dialog is moved to document.body on open, so modal nodes are not always inside this.root. */
  modalNodes(selector) {
    const nodes = [];
    [this.modal, this.root].forEach((root) => {
      if (!root) return;
      root.querySelectorAll(selector).forEach((el) => {
        if (!nodes.includes(el)) nodes.push(el);
      });
    });
    return nodes;
  }

  setSpec(spec) {
    this.spec = spec;
    if (this.sizeEl) this.sizeEl.textContent = spec.label;
    const label = spec.label ? `Previewing ${spec.label}` : '';
    this.modalNodes('[data-kjv-size]').forEach((el) => {
      el.textContent = label;
    });
    if (this.modal && this.modal.open && this.view === 'qr') this.renderQr();
    if (this.picker && !this.picker.hidden) this.renderPicker();
    const key = specKey(spec);
    const done = this.ready.get(key);
    if (done) {
      this.applyBuild(done);
      return;
    }
    this.setBusy(true);
    this.build(spec)
      .then((result) => {
        if (specKey(this.spec) === key) this.applyBuild(result);
      })
      .catch(() => {
        if (specKey(this.spec) === key) this.showStatus(this.root.dataset.messageModelError);
      });
  }

  applyBuild(result) {
    this.setBusy(false);
    if (this.viewer) {
      this.viewer.src = result.glbUrl;
      this.viewer.iosSrc = result.usdzUrl;
    }
  }

  /** The camera start button waits for the model: Quick Look has to launch inside the tap. */
  setBusy(busy) {
    this.modalNodes('[data-kjv-start]').forEach((button) => {
      button.disabled = busy;
      if (busy) button.setAttribute('aria-busy', 'true');
      else button.removeAttribute('aria-busy');
    });
  }

  texture(aspect) {
    const key = aspect.toFixed(3);
    if (!this.textures.has(key)) {
      if (!this.bitmap) this.bitmap = loadArtworkBitmap(this.imageUrl);
      const promise = this.bitmap.then((bitmap) => artworkTexture(bitmap, aspect));
      promise.catch(() => {
        this.textures.delete(key);
        this.bitmap = null;
      });
      this.textures.set(key, promise);
    }
    return this.textures.get(key);
  }

  build(spec) {
    const key = specKey(spec);
    if (!this.builds.has(key)) {
      const addFrame = spec.frame !== 'none';
      const promise = Promise.all([this.texture(spec.w / spec.h), addFrame ? makeFrameJpeg(spec.frame) : null]).then(
        ([imageBytes, frameBytes]) => {
          const result = {
            glbUrl: URL.createObjectURL(
              new Blob([buildGlb(imageBytes, frameBytes, spec.w, spec.h, addFrame, isAndroid())], {
                type: 'model/gltf-binary',
              }),
            ),
            usdzUrl: URL.createObjectURL(
              new Blob([buildUsdz(imageBytes, frameBytes, spec.w, spec.h, addFrame)], { type: 'model/vnd.usdz+zip' }),
            ),
          };
          this.ready.set(key, result);
          return result;
        },
      );
      promise.catch(() => this.builds.delete(key));
      this.builds.set(key, promise);
    }
    return this.builds.get(key);
  }

  usdzHref(url) {
    if (!url) return '';
    return url.includes('#') ? `${url}&allowsContentScaling=0` : `${url}#allowsContentScaling=0`;
  }

  warmModelViewer() {
    const warm = () => {
      loadModelViewer().catch(() => {});
      this.prepareAndroid();
    };
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(warm, { timeout: 2000 });
    } else {
      window.setTimeout(warm, 1);
    }
  }

  showStatus(message) {
    if (!this.statusEl) return;
    if (!message) {
      this.statusEl.hidden = true;
      this.statusEl.textContent = '';
      return;
    }
    this.statusEl.hidden = false;
    this.statusEl.textContent = message;
  }

  /* Modal */

  bindModal() {
    const modal = this.modal;
    if (!modal) return;
    this.views = new Map();
    modal.querySelectorAll('[data-kjv-view]').forEach((view) => this.views.set(view.dataset.kjvView, view));
    const actions = [
      ['data-kjv-close', () => this.closeModal()],
      ['data-kjv-start', () => this.start()],
      ['data-kjv-copy', (button) => this.copyLink(button)],
    ];
    modal.addEventListener('click', (event) => {
      if (event.target === modal) {
        this.closeModal();
        return;
      }
      const button = event.target instanceof Element ? event.target.closest('button') : null;
      if (!button || button.disabled) return;
      const action = actions.find(([attribute]) => button.hasAttribute(attribute));
      if (action) action[1](button);
    });
    modal.addEventListener('close', () => this.onModalClose());
  }

  openModal(view, opener = null) {
    const modal = this.modal;
    if (!modal) return;
    /* Safari paints only the gray backdrop when a dialog sits inside a transformed
       ancestor. Scroll animations leave translateZ(0) on the section, so lift the
       dialog out before opening it. */
    if (modal.parentElement !== document.body) document.body.appendChild(modal);
    if (!modal.open) {
      this.opener = opener || document.activeElement;
      modal.showModal();
      document.documentElement.classList.add('kjv-open');
    }
    this.showView(view);
  }

  showView(name) {
    if (!this.views || !this.views.has(name)) return;
    this.view = name;
    this.views.forEach((el, key) => {
      el.hidden = key !== name;
    });
    this.modal.dataset.view = name;
    this.modal.querySelectorAll('[data-kjv-copy-status]').forEach((el) => {
      el.textContent = '';
    });
    const focus = this.views.get(name).querySelector('[data-kjv-focus]');
    if (focus) {
      this.modal.setAttribute('aria-labelledby', focus.id);
      focus.focus({ preventScroll: true });
    }
    if (name === 'qr') this.renderQr();
  }

  closeModal() {
    if (this.modal && this.modal.open) this.modal.close();
  }

  onModalClose() {
    document.documentElement.classList.remove('kjv-open');
    const opener = this.opener;
    this.opener = null;
    if (opener && opener.isConnected && typeof opener.focus === 'function') opener.focus({ preventScroll: true });
  }

  /** Arrivals from the desktop QR code land on the camera view; AR itself waits for a tap. */
  handleArrival() {
    const params = new URLSearchParams(window.location.search);
    if (params.get('kj_ar') !== '1' || !this.modal) return;
    params.delete('kj_ar');
    const query = params.toString();
    window.history.replaceState(
      window.history.state,
      '',
      `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`,
    );
    const capable = supportsQuickLook() || isAndroid();
    if (!capable && !isPhone()) return;
    const open = () => this.openModal(capable ? 'camera' : 'unavailable');
    const css = document.querySelector('[data-kjv-css]');
    if (!css || css.sheet) open();
    else css.addEventListener('load', open, { once: true });
  }

  /* Live camera */

  /** Every "see it on your wall" button lands here. Quick Look and WebXR have to start inside the tap. */
  start(opener = null) {
    if (supportsQuickLook()) {
      const result = this.ready.get(specKey(this.spec));
      if (result) launchQuickLook(this.usdzHref(result.usdzUrl));
      else this.openModal('camera', opener);
      return;
    }
    if (isAndroid()) {
      this.launchAndroid(opener);
      return;
    }
    this.openModal(isPhone() ? 'unavailable' : 'qr', opener);
  }

  prepareAndroid() {
    if (!isAndroid() || this.viewer) return;
    const key = specKey(this.spec);
    const result = this.ready.get(key);
    (result ? Promise.resolve(result) : this.build(this.spec))
      .then((built) => loadModelViewer().then(() => this.ensureViewer(built)))
      .catch(() => {});
  }

  async launchAndroid(opener) {
    if (this.busy) return;
    this.busy = true;
    this.modalNodes('[data-kjv-start]').forEach((b) => b.setAttribute('aria-busy', 'true'));
    try {
      const result = this.ready.get(specKey(this.spec)) || (await this.build(this.spec));
      await loadModelViewer();
      const viewer = this.ensureViewer(result);
      await this.whenViewerReady(viewer);
      if (viewer.canActivateAR) {
        await viewer.activateAR();
        return;
      }
      this.openModal('unavailable', opener);
    } catch (error) {
      this.openModal('unavailable', opener);
    } finally {
      this.busy = false;
      this.modalNodes('[data-kjv-start]').forEach((b) => b.removeAttribute('aria-busy'));
    }
  }

  mobileUrl() {
    const url = new URL(window.location.href);
    url.hash = '';
    if (this.spec.id) url.searchParams.set('variant', this.spec.id);
    url.searchParams.set('kj_ar', '1');
    return url.toString();
  }

  async renderQr() {
    const box = this.modal.querySelector('[data-kjv-qr]');
    const hint = this.modal.querySelector('[data-kjv-qr-hint]');
    const input = this.modal.querySelector('[data-kjv-link]');
    const url = this.mobileUrl();
    if (input) input.value = url;
    if (!box || box.dataset.url === url) return;
    box.dataset.url = url;
    try {
      const { default: qrcode } = await import(this.root.dataset.qrSrc);
      const qr = qrcode(0, 'M');
      qr.addData(url);
      qr.make();
      const count = qr.getModuleCount();
      let path = '';
      for (let row = 0; row < count; row += 1) {
        for (let col = 0; col < count; col += 1) {
          if (qr.isDark(row, col)) path += `M${col} ${row}h1v1h-1z`;
        }
      }
      const size = count + 8;
      box.innerHTML = `<svg viewBox="-4 -4 ${size} ${size}" shape-rendering="crispEdges" aria-hidden="true"><rect x="-4" y="-4" width="${size}" height="${size}" fill="#fff"/><path d="${path}" fill="#262228"/></svg>`;
      box.hidden = false;
      if (hint) hint.textContent = "Point your phone's camera at the code.";
    } catch (error) {
      box.hidden = true;
      delete box.dataset.url;
      if (hint) hint.textContent = 'Open this link on your phone:';
    }
  }

  async copyLink(button) {
    const url = this.mobileUrl();
    const status = button.closest('[data-kjv-view]').querySelector('[data-kjv-copy-status]');
    let copied = false;
    try {
      await navigator.clipboard.writeText(url);
      copied = true;
    } catch (error) {
      const input = this.modal.querySelector('[data-kjv-link]');
      if (input && !input.closest('[hidden]')) {
        input.value = url;
        input.select();
        copied = document.execCommand('copy');
      }
    }
    if (status) status.textContent = copied ? 'Link copied. Open it on your phone.' : url;
  }

  ensureViewer(result) {
    if (this.viewer && this.viewer.isConnected) return this.viewer;

    const viewer = document.createElement('model-viewer');
    viewer.src = result.glbUrl;
    viewer.iosSrc = result.usdzUrl;
    viewer.alt = this.root.dataset.artworkAlt || 'Artwork preview';
    viewer.setAttribute('ar', '');
    viewer.setAttribute('ar-modes', isAndroid() ? 'webxr' : 'webxr quick-look');
    viewer.setAttribute('ar-placement', 'wall');
    viewer.setAttribute('ar-scale', 'fixed');
    if (isAndroid()) viewer.setAttribute('xr-environment', '');
    viewer.setAttribute('camera-controls', '');
    viewer.setAttribute('touch-action', 'pan-y');
    viewer.setAttribute('environment-image', 'neutral');
    viewer.setAttribute('shadow-intensity', '0.3');
    viewer.setAttribute('shadow-softness', '0.8');
    if (!prefersReducedMotion()) viewer.setAttribute('auto-rotate', '');

    const hiddenArButton = document.createElement('button');
    hiddenArButton.type = 'button';
    hiddenArButton.slot = 'ar-button';
    hiddenArButton.hidden = true;
    hiddenArButton.tabIndex = -1;
    hiddenArButton.setAttribute('aria-hidden', 'true');
    viewer.appendChild(hiddenArButton);

    viewer.addEventListener('error', () => {
      this.showStatus(this.root.dataset.messageModelError);
    });

    if (this.viewerHost) this.viewerHost.replaceChildren(viewer);
    else this.root.appendChild(viewer);

    this.viewer = viewer;
    return viewer;
  }

  whenViewerReady(viewer) {
    if (viewer.canActivateAR || viewer.loaded) return Promise.resolve();
    return Promise.race([
      new Promise((resolve) => viewer.addEventListener('load', resolve, { once: true })),
      new Promise((resolve) => window.setTimeout(resolve, 400)),
    ]);
  }

  destroy() {
    this.closeModal();
    document.documentElement.classList.remove('kjv-open');
  }
}

function initArtworkVisualisers(scope = document) {
  scope.querySelectorAll('[data-artwork-visualiser]').forEach((root) => {
    if (!(root instanceof HTMLElement) || root.dataset.artworkBound === 'true') return;
    root.dataset.artworkBound = 'true';
    const visualiser = new ArtworkVisualiser(root);
    root.kjVisualiser = visualiser;
    bindWallTriggers(visualiser);
  });
}

function bindWallTriggers(visualiser) {
  const productId = visualiser.root.dataset.productId;
  document.querySelectorAll('[data-kj-wall-trigger]').forEach((trigger) => {
    if (!(trigger instanceof HTMLElement) || trigger.dataset.productId !== productId) return;
    const bound = Boolean(trigger.kjVisualiser);
    trigger.kjVisualiser = visualiser;
    trigger.hidden = false;
    trigger.setAttribute('aria-haspopup', 'dialog');
    if (!bound) {
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        trigger.kjVisualiser.start(trigger);
      });
    }
  });
}

initArtworkVisualisers();

document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof HTMLElement) initArtworkVisualisers(event.target);
});

document.addEventListener('shopify:section:unload', (event) => {
  if (!(event.target instanceof HTMLElement)) return;
  event.target.querySelectorAll('[data-artwork-visualiser]').forEach((root) => {
    if (root.kjVisualiser) root.kjVisualiser.destroy();
  });
});
